import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Company } from '../../company/company.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { CrmService, TransporterFleetOption } from '../../crm/crm.service';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { EntityStatusService } from '../../services/entity-status.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { isSisterCompanyResource, resolveSisterCompanyScope, sisterCompanyFleetAccessMessage, SisterCompanyScope } from '../../shared/utils/sister-company-scope.util';
import { Driver } from './driver.model';
import { DriverService } from './driver.service';
import { FleetResourceSource, QuickFleetOnboardingDialogComponent } from '../quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { FleetOwnershipTransferDialogComponent, OwnershipTransferResource } from '../ownership-transfer/fleet-ownership-transfer-dialog.component';

@Component({
    selector: 'app-driver',
    templateUrl: './driver.component.html',
    styleUrls: ['./driver.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        BreadcrumbModule,
        InputTextModule,
        DialogModule,
        ConfirmDialogModule,
        TabsModule,
        ProgressBarModule,
        ChipModule,
        TagModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        DatePickerModule,
        SelectModule,
        SelectButtonModule,
        SplitButtonModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        DashboardCompanyScopeSelectorComponent,
        QuickFleetOnboardingDialogComponent,
        FleetOwnershipTransferDialogComponent
    ]
})
export class DriverComponent implements OnInit {
    private readonly tabKeys = ['owned', 'freelancer', 'rental', 'transporter'] as const;
    readonly resourceViewOptions = [
        { label: 'Current Company', value: 'current' },
        { label: 'Sister Companies', value: 'sister' }
    ];

    isLoading = false;
    quickFleetOnboardingVisible = false;
    quickFleetOnboardingSource: FleetResourceSource = 'owned';
    ownershipTransferVisible = false;
    ownershipTransferResources: OwnershipTransferResource[] = [];
    ownershipTransferPairedResource: OwnershipTransferResource | null = null;
    readonly createDriverItems: MenuItem[] = [
        { label: 'Quick onboard driver & vehicle', icon: 'pi pi-sparkles', command: () => this.openQuickFleetOnboarding('owned') },
        { label: 'Add freelancer driver', icon: 'pi pi-user-plus', command: () => this.addFreelancerDriver() },
        { label: 'Onboard rental pair', icon: 'pi pi-key', command: () => this.openQuickFleetOnboarding('rental') },
        { label: 'Onboard transporter pair', icon: 'pi pi-building', command: () => this.openQuickFleetOnboarding('transporter') },
        { separator: true },
        { label: 'Bulk upload drivers', icon: 'pi pi-upload', command: () => this.openBulkUpload() }
    ];
    private pendingLoads = 0;
    currentTab: 'owned' | 'freelancer' | 'rental' | 'transporter' = 'owned';
    activeTabIndex = 0;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    currentCompanyId: number | null = null;
    resourceView: 'current' | 'sister' = 'current';
    private sisterAccessErrorShown = false;
    sisterCompanyScope: SisterCompanyScope | null = null;
    private resourceScopeVersion = 0;
    selectedSisterCompanyId: number | null = null;

    ownedDriverSource: Driver[] = [];
    ownedDrivers: Driver[] = [];
    ownedServerRecords = 0;
    totalOwnedRecords = 0;
    ownedFirst = 0;
    ownedRows = 10;

    freelancerDriverSource: Driver[] = [];
    freelancerDrivers: Driver[] = [];
    freelancerFirst = 0;
    freelancerServerRecords = 0;
    totalFreelancerRecords = 0;
    freelancerRows = 10;

    rentalDriverSource: Driver[] = [];
    rentalDrivers: Driver[] = [];
    rentalFirst = 0;
    rentalServerRecords = 0;
    totalRentalRecords = 0;
    rentalRows = 10;

    transporterOptions: TransporterFleetOption[] = [];
    selectedTransporterCrmClientId: number | null = null;
    transporterDriverSource: Driver[] = [];
    transporterDrivers: Driver[] = [];
    transporterFirst = 0;
    transporterServerRecords = 0;
    totalTransporterRecords = 0;
    transporterRows = 10;

    driverSearchText = '';
    driverPhoneSearch = '';
    driverIqamaSearch = '';
    driverLicenseSearch = '';
    driverVehicleSearch = '';
    driverStatusSearch: string | null = null;
    readonly driverStatusOptions = [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Inactive', value: 'INACTIVE' },
        { label: 'On Hold', value: 'ON_HOLD' },
        { label: 'Suspended', value: 'SUSPENDED' }
    ];
    private driverStatusUpdating = new Set<number>();

    showAddFreelancerDriverDialog = false;
    driverPhoneNumber = '';
    searchDriverInProgress = false;

    canViewDrivers = true;
    canCreateDrivers = true;
    canEditDrivers = true;
    canDeleteDrivers = true;

    constructor(
        private driverService: DriverService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private entityStatusService: EntityStatusService,
        private tenantScopeService: TenantScopeService,
        private crmService: CrmService
    ) { }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id ?? null;
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Drivers' }];
        this.initializeSisterScope();
        this.route.queryParamMap.subscribe(queryParams => {
            const previousView = this.resourceView;
            const previousCompanyId = this.selectedSisterCompanyId;
            this.applyRouteState(queryParams);
            if (previousView !== this.resourceView || previousCompanyId !== this.selectedSisterCompanyId) {
                this.resetResourceScope(false);
                this.reloadAllDriverLists();
            }
        });
    }

    get sisterCompanies(): Company[] {
        return this.sisterCompanyScope?.sisterCompanies ?? [];
    }

    get canUseSisterCompanyMode(): boolean {
        return !!this.sisterCompanyScope;
    }

    get isSisterMode(): boolean {
        return this.resourceView === 'sister' && this.canUseSisterCompanyMode;
    }

    get sisterCompanyFilterHint(): string {
        const groupName = this.sisterCompanyScope?.companyGroupName || 'the group';
        return `Default view shows all accessible sister companies in ${groupName}.`;
    }

    get sisterCompanyAllLabel(): string {
        return 'All sister companies';
    }

    get selectedTransporterName(): string {
        return this.transporterOptions.find(option => option.crmClientId === this.selectedTransporterCrmClientId)
            ?.transporterName || 'Transporter agency';
    }

    editDriver(driver: Driver): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/driver/edit', driver.id], {
            queryParams: this.buildDriverRouteQueryParams(driver)
        });
    }

    viewDriver(driver: Driver): void {
        this.router.navigate(['/fleet/driver/view', driver.id], {
            queryParams: this.buildDriverRouteQueryParams(driver)
        });
    }

    changeDriverOwnership(driver: Driver): void {
        if (!this.currentCompanyId || this.isSisterMode || !driver?.id) return;
        this.ownershipTransferResources = [{ resourceType: 'DRIVER', resourceId: driver.id, label: driver.name || `Driver #${driver.id}` }];
        const vehicle = driver.currentVehicle;
        this.ownershipTransferPairedResource = vehicle?.vehicleId
            ? { resourceType: 'VEHICLE', resourceId: vehicle.vehicleId, label: vehicle.plateNumber || `Vehicle #${vehicle.vehicleId}` }
            : null;
        this.ownershipTransferVisible = true;
    }

    onOwnershipTransferCompleted(): void {
        this.messageService.add({ severity: 'success', summary: 'Ownership updated', detail: 'Fleet ownership was corrected without recreating the resource.' });
        this.reloadAllDriverLists();
    }

    deleteDriver(driver: Driver): void {
        if (this.isSisterMode) {
            return;
        }
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this driver?',
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.driverService.deleteDriver(driver).subscribe(response => {
                    if (response.success) {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Driver deleted successfully.' });
                        this.reloadCurrentTab();
                    } else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete driver.' });
                    }
                });
            }
        });
    }

    deactivateRentalDriver(driver: Driver): void {
        if (this.isSisterMode) {
            return;
        }
        this.confirmationService.confirm({
            message: 'Are you sure you want to remove this rental driver from your company?',
            header: 'Confirm Rental Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const companyId = this.currentCompanyId;
                if (!companyId || !driver.id) {
                    return;
                }
                this.driverService.deactivateRentalDriver(driver.id, companyId).subscribe(response => {
                    if (response.success) {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Rental driver removed successfully.' });
                        this.loadRentalDrivers();
                    } else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove rental driver.' });
                    }
                });
            }
        });
    }

    loadOwnedData(event: TableLazyLoadEvent): void {
        this.ownedFirst = event.first ?? 0;
        this.ownedRows = event.rows ?? 10;
        this.loadOwnedDrivers();
    }

    loadFreelancerData(event: TableLazyLoadEvent): void {
        this.freelancerFirst = event.first ?? 0;
        this.freelancerRows = event.rows ?? 10;
        this.loadFreelancerDrivers();
    }

    loadRentalData(event: TableLazyLoadEvent): void {
        this.rentalFirst = event.first ?? 0;
        this.rentalRows = event.rows ?? 10;
        this.loadRentalDrivers();
    }

    loadTransporterData(event: TableLazyLoadEvent): void {
        this.transporterFirst = event.first ?? 0;
        this.transporterRows = event.rows ?? 10;
        this.loadTransporterDrivers();
    }

    onTransporterChange(transporterCrmClientId: number | null): void {
        this.selectedTransporterCrmClientId = transporterCrmClientId ? Number(transporterCrmClientId) : null;
        this.transporterFirst = 0;
        this.syncRouteState();
        this.loadTransporterDrivers();
    }

    tabChange(event: string | number): void {
        this.activeTabIndex = Number(event);
        this.currentTab = this.tabKeys[this.activeTabIndex] ?? 'owned';
        this.syncRouteState();
        this.reloadCurrentTab();
    }

    onResourceViewChange(value: 'current' | 'sister'): void {
        this.resourceView = value === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
        if (!this.isSisterMode) this.selectedSisterCompanyId = null;
        this.resetResourceScope();
        this.syncRouteState();
        this.reloadAllDriverLists();
    }

    onSisterCompanyChange(companyId: number | null): void {
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(companyId) ? companyId : null;
        this.resetResourceScope();
        this.syncRouteState();
        this.reloadAllDriverLists();
    }

    addFreelancerDriver(): void {
        if (this.isSisterMode) {
            return;
        }
        this.showAddFreelancerDriverDialog = true;
    }

    addRentalDriver(): void {
        this.openQuickFleetOnboarding('rental');
    }

    searchDriver(): void {
        if (this.isSisterMode) {
            return;
        }
        this.searchDriverInProgress = true;
        this.driverService.findDriverByFilter(undefined, undefined, undefined, this.driverPhoneNumber, false, 'en-US')
            .subscribe(driverWrapped => {
                if (driverWrapped.success && driverWrapped.data) {
                    this.addFreelanceDriver(driverWrapped.data);
                } else {
                    this.searchDriverInProgress = false;
                    this.router.navigate(['/fleet/driver/create'], {
                        queryParams: {
                            phoneNumber: this.driverPhoneNumber,
                            isFreelancer: true
                        }
                    });
                }
            });
    }

    addFreelanceDriver(driver: Driver): void {
        if (!this.currentCompanyId) {
            return;
        }
        this.driverService.addFreelanceDriver(driver, this.currentCompanyId)
            .subscribe(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Driver added as freelancer successfully.'
                });
                this.showAddFreelancerDriverDialog = false;
                this.searchDriverInProgress = false;
                this.loadFreelancerDrivers();
            }, () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to add driver as freelancer.'
                });
            });
    }

    addOwnDriver(): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/driver/create']);
    }

    openQuickFleetOnboarding(source: FleetResourceSource = 'owned'): void {
        if (this.isSisterMode) {
            return;
        }
        this.quickFleetOnboardingSource = source;
        this.quickFleetOnboardingVisible = true;
    }

    onQuickFleetOnboarded(): void {
        this.reloadAllDriverLists();
    }

    openBulkUpload(): void {
        if (this.isSisterMode) {
            return;
        }
        this.router.navigate(['/fleet/bulk-upload']);
    }

    driverMetrics(): PageMetric[] {
        const labelPrefix = this.isSisterMode ? 'Sister ' : '';
        const hintPrefix = this.isSisterMode ? 'Other company ' : '';
        return [
            {
                label: `${labelPrefix}Owned Drivers`,
                value: this.totalOwnedRecords,
                hint: `${hintPrefix}driver records`,
                tone: 'info'
            },
            {
                label: `${labelPrefix}Freelancers`,
                value: this.totalFreelancerRecords,
                hint: `${hintPrefix}external drivers`,
                tone: 'success'
            },
            {
                label: `${labelPrefix}Rental Drivers`,
                value: this.totalRentalRecords,
                hint: `${hintPrefix}rented capacity links`,
                tone: 'warn'
            },
            {
                label: 'Transporter Drivers',
                value: this.totalTransporterRecords,
                hint: 'External agency drivers',
                tone: 'info'
            }
        ];
    }

    activeResultText(): string {
        const scopeLabel = this.isSisterMode ? 'sister-company ' : '';
        if (this.activeTabIndex === 0) {
            return `${this.totalOwnedRecords} ${scopeLabel}owned drivers`;
        }
        if (this.activeTabIndex === 2) {
            return `${this.totalRentalRecords} ${scopeLabel}rental drivers`;
        }
        if (this.activeTabIndex === 3) {
            return `${this.totalTransporterRecords} transporter drivers`;
        }
        return `${this.totalFreelancerRecords} ${scopeLabel}freelance drivers`;
    }

    activeDriverFilterCount(): number {
        return [
            this.driverSearchText,
            this.driverPhoneSearch,
            this.driverIqamaSearch,
            this.driverLicenseSearch,
            this.driverVehicleSearch,
            this.driverStatusSearch || ''
        ].filter(value => value?.trim()).length;
    }

    clearDriverFilters(): void {
        this.driverSearchText = '';
        this.driverPhoneSearch = '';
        this.driverIqamaSearch = '';
        this.driverLicenseSearch = '';
        this.driverVehicleSearch = '';
        this.driverStatusSearch = null;
        this.applyDriverFilters();
    }

    updateDriverStatus(driver: Driver, status: string): void {
        if (this.isSisterMode || !driver.id || driver.status === status) {
            return;
        }

        const previousStatus = driver.status || 'ACTIVE';
        driver.status = status;
        this.driverStatusUpdating.add(driver.id);

        this.entityStatusService.setStatus('DRIVER', driver.id, status, 'Portal driver status update').subscribe({
            next: () => {
                this.driverStatusUpdating.delete(driver.id!);
                this.messageService.add({ severity: 'success', summary: 'Success', detail: `Driver marked ${this.formatDriverStatus(status)}.` });
                this.applyDriverFilters();
            },
            error: () => {
                driver.status = previousStatus;
                this.driverStatusUpdating.delete(driver.id!);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update driver status.' });
            }
        });
    }

    isDriverStatusUpdating(driver: Driver): boolean {
        return !!driver.id && this.driverStatusUpdating.has(driver.id);
    }

    driverStatusSeverity(status?: string): 'success' | 'danger' | 'warn' | 'secondary' {
        if (status === 'INACTIVE' || status === 'SUSPENDED') {
            return 'danger';
        }
        if (status === 'ON_HOLD') {
            return 'warn';
        }
        return status ? 'success' : 'secondary';
    }

    formatDriverStatus(status?: string): string {
        return (status || 'ACTIVE').replace(/_/g, ' ');
    }

    applyDriverFilters(): void {
        this.ownedDrivers = this.filterDrivers(this.ownedDriverSource);
        this.freelancerDrivers = this.filterDrivers(this.freelancerDriverSource);
        this.rentalDrivers = this.filterDrivers(this.rentalDriverSource);
        this.transporterDrivers = this.filterDrivers(this.transporterDriverSource);
        if (this.activeDriverFilterCount() > 0) {
            this.totalOwnedRecords = this.ownedDrivers.length;
            this.totalFreelancerRecords = this.freelancerDrivers.length;
            this.totalRentalRecords = this.rentalDrivers.length;
            this.totalTransporterRecords = this.transporterDrivers.length;
        } else {
            this.totalOwnedRecords = this.ownedServerRecords;
            this.totalFreelancerRecords = this.freelancerServerRecords;
            this.totalRentalRecords = this.rentalServerRecords;
            this.totalTransporterRecords = this.transporterServerRecords;
        }
    }

    isRentalExpired(driver: Driver): boolean {
        return !!driver.rentalEndDate && new Date(driver.rentalEndDate).getTime() < new Date().setHours(0, 0, 0, 0);
    }

    private initializeSisterScope(): void {
        const applyScope = () => {
            this.sisterCompanyScope = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.drivers.view']
            );
            this.applyRouteState(this.route.snapshot.queryParamMap);
            this.reloadAllDriverLists();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private applyRouteState(queryParams: ParamMap): void {
        const tabIndex = this.resolveTabIndex(queryParams.get('tab'));
        this.activeTabIndex = tabIndex;
        this.currentTab = this.tabKeys[tabIndex];

        const requestedResourceView = queryParams.get('resourceView');
        this.resourceView = requestedResourceView === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';

        const requestedMemberCompanyId = this.toOptionalNumber(queryParams.get('memberCompanyId'));
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(requestedMemberCompanyId) ? requestedMemberCompanyId : null;
        this.selectedTransporterCrmClientId = this.toOptionalNumber(queryParams.get('transporterCrmClientId'));
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
        }
    }

    private resolveTabIndex(tabKey: string | null): number {
        if (!tabKey) {
            return 0;
        }
        const tabIndex = this.tabKeys.findIndex(key => key === tabKey.toLowerCase());
        return tabIndex >= 0 ? tabIndex : 0;
    }

    private syncRouteState(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                tab: this.currentTab,
                resourceView: this.resourceView,
                memberCompanyId: this.isSisterMode ? this.selectedSisterCompanyId : null,
                transporterCrmClientId: this.currentTab === 'transporter' ? this.selectedTransporterCrmClientId : null
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private loadOwnedDrivers(): void {
        const page = this.resolvePage(this.ownedFirst, this.ownedRows);
        const pageSize = this.ownedRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetOwnedDrivers();
                return;
            }
            this.bindDriverRequest(
                this.driverService.findDriversByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.ownedDriverSource = this.normalizeDriverStatuses(response.items);
                    this.ownedServerRecords = response.count;
                    this.applyDriverFilters();
                }
            );
            return;
        }

        const transportationAgencyId = this.sessionStorageService.getCompany()?.transportationAgencyId;
        if (!transportationAgencyId) {
            this.resetOwnedDrivers();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Transportation Agency ID not found.' });
            return;
        }
        this.bindDriverRequest(
            this.driverService.findDriversByTransportationAgencyId(page, pageSize, transportationAgencyId),
            response => {
                this.ownedDriverSource = this.normalizeDriverStatuses(response.items);
                this.ownedServerRecords = response.count;
                this.applyDriverFilters();
                this.hydrateDriverStatuses(this.ownedDriverSource);
            }
        );
    }

    private loadFreelancerDrivers(): void {
        const page = this.resolvePage(this.freelancerFirst, this.freelancerRows);
        const pageSize = this.freelancerRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetFreelancerDrivers();
                return;
            }
            this.bindDriverRequest(
                this.driverService.getFreelanceDriversByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.freelancerDriverSource = this.normalizeDriverStatuses(response.items);
                    this.freelancerServerRecords = response.count;
                    this.applyDriverFilters();
                }
            );
            return;
        }

        const companyId = this.currentCompanyId;
        if (!companyId) {
            this.resetFreelancerDrivers();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            return;
        }
        this.bindDriverRequest(
            this.driverService.getFreelanceDriversByCompanyId(page, pageSize, companyId),
            response => {
                this.freelancerDriverSource = this.normalizeDriverStatuses(response.items);
                this.freelancerServerRecords = response.count;
                this.applyDriverFilters();
                this.hydrateDriverStatuses(this.freelancerDriverSource);
            }
        );
    }

    private loadRentalDrivers(): void {
        const page = this.resolvePage(this.rentalFirst, this.rentalRows);
        const pageSize = this.rentalRows || 10;
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope || !this.currentCompanyId) {
                this.resetRentalDrivers();
                return;
            }
            this.bindDriverRequest(
                this.driverService.getRentalDriversByCompanyGroup(
                    page,
                    pageSize,
                    this.sisterCompanyScope.companyGroupId,
                    this.currentCompanyId,
                    this.selectedSisterCompanyId
                ),
                response => {
                    this.rentalDriverSource = this.normalizeDriverStatuses(response.items);
                    this.rentalServerRecords = response.count;
                    this.applyDriverFilters();
                }
            );
            return;
        }

        const companyId = this.currentCompanyId;
        if (!companyId) {
            this.resetRentalDrivers();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            return;
        }
        this.bindDriverRequest(
            this.driverService.getRentalDriversByCompanyId(page, pageSize, companyId),
            response => {
                this.rentalDriverSource = this.normalizeDriverStatuses(response.items);
                this.rentalServerRecords = response.count;
                this.applyDriverFilters();
                this.hydrateDriverStatuses(this.rentalDriverSource);
            }
        );
    }

    private loadTransporterOptions(): void {
        if (!this.currentCompanyId) {
            return;
        }
        const scopeVersion = this.resourceScopeVersion;
        this.crmService.getScopedTransporters(this.currentCompanyId).subscribe({
            next: options => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                this.transporterOptions = (options || []).filter(option => !this.isSisterMode
                    || isSisterCompanyResource(option.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId));
                if (this.selectedTransporterCrmClientId != null
                    && !this.transporterOptions.some(option => option.crmClientId === this.selectedTransporterCrmClientId)) {
                    this.selectedTransporterCrmClientId = null;
                }
                this.loadTransporterDrivers();
            },
            error: () => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                this.transporterOptions = [];
                this.resetTransporterDrivers();
                this.messageService.add({ severity: 'error', summary: 'Transporters unavailable', detail: 'Unable to load accessible transporter agencies.' });
            }
        });
    }

    private loadTransporterDrivers(): void {
        if (!this.currentCompanyId || !this.selectedTransporterCrmClientId) {
            this.resetTransporterDrivers();
            return;
        }
        const page = this.resolvePage(this.transporterFirst, this.transporterRows);
        const pageSize = this.transporterRows || 10;
        this.bindDriverRequest(
            this.driverService.getTransporterDrivers(page, pageSize, this.currentCompanyId, this.selectedTransporterCrmClientId),
            response => {
                this.transporterDriverSource = this.normalizeDriverStatuses(response.items);
                this.transporterServerRecords = response.count;
                this.applyDriverFilters();
            }
        );
    }

    private bindDriverRequest(request: Observable<APIListWrapper<Driver>>, next: (response: APIListWrapper<Driver>) => void): void {
        const scopeVersion = this.resourceScopeVersion;
        this.beginPageLoad();
        request.pipe(finalize(() => this.endPageLoad())).subscribe({
            next: response => { if (scopeVersion === this.resourceScopeVersion) next(response); },
            error: error => {
                if (scopeVersion !== this.resourceScopeVersion) return;
                if (error?.status === 403 && this.isSisterMode) {
                    if (!this.sisterAccessErrorShown) {
                        this.sisterAccessErrorShown = true;
                        this.resetOwnedDrivers();
                        this.resetFreelancerDrivers();
                        this.resetRentalDrivers();
                        this.messageService.add({ severity: 'error', summary: 'Sister-company driver access denied', detail: sisterCompanyFleetAccessMessage('driver') });
                    }
                    return;
                }
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load drivers.' });
            }
        });
    }

    private resetResourceScope(clearTransporterSelection = true): void {
        this.resourceScopeVersion++;
        this.ownedFirst = this.freelancerFirst = this.rentalFirst = this.transporterFirst = 0;
        this.resetOwnedDrivers();
        this.resetFreelancerDrivers();
        this.resetRentalDrivers();
        this.resetTransporterDrivers();
        this.transporterOptions = [];
        if (clearTransporterSelection) this.selectedTransporterCrmClientId = null;
    }

    private reloadAllDriverLists(): void {
        this.sisterAccessErrorShown = false;
        this.loadOwnedDrivers();
        this.loadFreelancerDrivers();
        this.loadRentalDrivers();
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
        }
    }

    private reloadCurrentTab(): void {
        this.sisterAccessErrorShown = false;
        if (this.currentTab === 'owned') {
            this.loadOwnedDrivers();
            return;
        }
        if (this.currentTab === 'rental') {
            this.loadRentalDrivers();
            return;
        }
        if (this.currentTab === 'transporter') {
            this.loadTransporterOptions();
            return;
        }
        this.loadFreelancerDrivers();
    }

    private filterDrivers(drivers: Driver[]): Driver[] {
        const keyword = this.normalizeSearch(this.driverSearchText);
        const phone = this.normalizeSearch(this.driverPhoneSearch);
        const iqama = this.normalizeSearch(this.driverIqamaSearch);
        const license = this.normalizeSearch(this.driverLicenseSearch);
        const vehicle = this.normalizeSearch(this.driverVehicleSearch);

        return drivers.filter(driver => {
            const searchable = this.normalizeSearch([
                driver.name,
                driver.emailAddress,
                driver.phoneNumber,
                driver.nationality?.name,
                driver.residingCountry?.name,
                driver.preferredAddress,
                driver.resourceCompanyName,
                driver.residenceNumber,
                driver.licenseNumber,
                driver.currentVehicle?.plateNumber,
                driver.currentVehicle?.registrationNumber,
                driver.rentalVendorName,
                driver.rentalContractReferenceNumber,
                driver.rentalRenewalStatus,
                driver.rentalStatus,
                driver.rentalNotes,
                driver.status
            ].join(' '));

            return (!keyword || searchable.includes(keyword))
                && (!phone || this.normalizeSearch(driver.phoneNumber).includes(phone))
                && (!iqama || this.normalizeSearch(driver.residenceNumber).includes(iqama))
                && (!license || this.normalizeSearch(driver.licenseNumber).includes(license))
                && (!vehicle || this.normalizeSearch(`${driver.currentVehicle?.plateNumber || ''} ${driver.currentVehicle?.registrationNumber || ''}`).includes(vehicle))
                && (!this.driverStatusSearch || driver.status === this.driverStatusSearch);
        });
    }

    private normalizeSearch(value?: string): string {
        return (value || '').toString().trim().toLowerCase();
    }

    private normalizeDriverStatuses(drivers: Driver[] = []): Driver[] {
        return drivers.map(driver => ({
            ...driver,
            status: driver.status || 'ACTIVE',
            tableRowKey: `${driver.id ?? 'driver'}-${driver.resourceCompanyId ?? 'current'}`
        }));
    }

    private hydrateDriverStatuses(drivers: Driver[]): void {
        if (this.isSisterMode) {
            return;
        }

        const driversWithId = drivers.filter(driver => !!driver.id);
        if (!driversWithId.length) {
            return;
        }

        forkJoin(
            driversWithId.map(driver =>
                this.entityStatusService.getStatus('DRIVER', driver.id!)
                    .pipe(catchError(() => of(driver.status || 'ACTIVE')))
            )
        ).subscribe(statuses => {
            statuses.forEach((status, index) => {
                driversWithId[index].status = status || 'ACTIVE';
            });
            this.applyDriverFilters();
        });
    }

    private resolvePage(first: number, rows: number): number {
        if (!rows) {
            return 0;
        }
        return Math.floor((first || 0) / rows);
    }

    private toOptionalNumber(value: string | null): number | null {
        if (!value) {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private isValidSisterCompanyId(companyId: number | null): boolean {
        return companyId != null && this.sisterCompanies.some(company => company.id === companyId);
    }

    private resetOwnedDrivers(): void {
        this.ownedDriverSource = [];
        this.ownedDrivers = [];
        this.ownedServerRecords = 0;
        this.totalOwnedRecords = 0;
    }

    private resetFreelancerDrivers(): void {
        this.freelancerDriverSource = [];
        this.freelancerDrivers = [];
        this.freelancerServerRecords = 0;
        this.totalFreelancerRecords = 0;
    }

    private resetRentalDrivers(): void {
        this.rentalDriverSource = [];
        this.rentalDrivers = [];
        this.rentalServerRecords = 0;
        this.totalRentalRecords = 0;
    }

    private resetTransporterDrivers(): void {
        this.transporterDriverSource = [];
        this.transporterDrivers = [];
        this.transporterServerRecords = 0;
        this.totalTransporterRecords = 0;
    }

    private beginPageLoad(): void {
        this.pendingLoads += 1;
        this.isLoading = true;
    }

    private endPageLoad(): void {
        this.pendingLoads = Math.max(0, this.pendingLoads - 1);
        this.isLoading = this.pendingLoads > 0;
    }

    private buildDriverRouteQueryParams(driver: Driver): Record<string, string | number> {
        const queryParams: Record<string, string | number> = {
            tab: this.currentTab,
            resourceView: this.resourceView
        };

        if (this.isSisterMode && this.selectedSisterCompanyId != null) {
            queryParams['memberCompanyId'] = this.selectedSisterCompanyId;
        }
        if (driver.resourceCompanyId != null) {
            queryParams['resourceCompanyId'] = driver.resourceCompanyId;
        }
        if (driver.resourceCompanyName) {
            queryParams['resourceCompanyName'] = driver.resourceCompanyName;
        }
        if (this.currentTab === 'freelancer') {
            queryParams['source'] = 'freelance';
        } else if (this.currentTab === 'rental') {
            queryParams['source'] = 'rental';
        } else if (this.currentTab === 'transporter') {
            queryParams['source'] = 'transporter';
            if (this.selectedTransporterCrmClientId != null) {
                queryParams['transporterCrmClientId'] = this.selectedTransporterCrmClientId;
            }
        } else {
            queryParams['source'] = 'owned';
        }
        return queryParams;
    }
}
